// src/pages/Admin/Appointment/AllInvoice.jsx
import React, { useState, useEffect, useContext } from "react";
import { TranslatorContext } from "../../../context/Translator";
import axios from "axios";
import { ButtonComponent } from "../../../components/elements";
import { Modal, Row, Col, Form, Alert, Spinner, Badge, Table } from "react-bootstrap";
import PageLayout from "../../../layouts/PageLayout";
import { Link } from "react-router-dom";
import { FaEye, FaFileInvoice, FaPaw, FaUserMd, FaClock, FaMoneyBillWave, FaPrint, FaDownload, FaStethoscope, FaFlask, FaXRay, FaCut, FaSyringe, FaHeartbeat, FaPills, FaPlus, FaEdit, FaSave, FaTimes, FaTrashAlt } from "react-icons/fa";
import { MdDateRange, MdAccessTime } from "react-icons/md";
import Swal from 'sweetalert2';
import '../App/modalstyle.css';

export default function AllInvoice() {
    const { t } = useContext(TranslatorContext);

    const [invoices, setInvoices] = useState([]);
    const [loading, setLoading] = useState(true);
    const [selectedInvoice, setSelectedInvoice] = useState(null);
    const [showInvoiceModal, setShowInvoiceModal] = useState(false);

    const permissions = JSON.parse(localStorage.getItem("permissions")) || [];

    const hasPermission = (perm) => {
        return permissions.includes(perm);
    };

    // Payment states
    const [showPaymentModal, setShowPaymentModal] = useState(false);
    const [payments, setPayments] = useState([
        { payment_method_id: "", amount: "", isEditing: false, id: null }
    ]);
    const [paymentMethods, setPaymentMethods] = useState([]);
    const [loadingMethods, setLoadingMethods] = useState(false);
    const [submitting, setSubmitting] = useState(false);
    const [editingPaymentId, setEditingPaymentId] = useState(null);

    // Pagination
    const [currentPage, setCurrentPage] = useState(1);
    const [lastPage, setLastPage] = useState(1);
    const [total, setTotal] = useState(0);
    const [perPage, setPerPage] = useState(10);

    useEffect(() => {
        fetchInvoices();
        fetchPaymentMethods();
    }, [currentPage]);

    const fetchInvoices = async () => {
        setLoading(true);
        const token = localStorage.getItem("authToken");

        try {
            const response = await axios.get(
                `${process.env.REACT_APP_API_URL}/all-invoice`,
                { headers: { Authorization: `Bearer ${token}` } }
            );

            if (response.data.success) {
                setInvoices(response.data.invoice || []);
                setTotal(response.data.invoice?.length || 0);
                setLastPage(Math.ceil((response.data.invoice?.length || 0) / perPage));
            }
        } catch (error) {
            console.error("Error fetching invoices:", error);
            Swal.fire({
                icon: 'error',
                title: 'Error',
                text: 'Failed to load invoices',
                confirmButtonColor: '#3085d6'
            });
        } finally {
            setLoading(false);
        }
    };

    const fetchPaymentMethods = async () => {
        try {
            setLoadingMethods(true);
            const token = localStorage.getItem("authToken");
            const response = await axios.get(
                `${process.env.REACT_APP_API_URL}/payment-methods`,
                { headers: { Authorization: `Bearer ${token}` } }
            );

            if (response.data) {
                if (Array.isArray(response.data)) {
                    setPaymentMethods(response.data);
                } else if (response.data.paymentMethods && Array.isArray(response.data.paymentMethods)) {
                    setPaymentMethods(response.data.paymentMethods);
                } else if (response.data.data && Array.isArray(response.data.data)) {
                    setPaymentMethods(response.data.data);
                } else {
                    setPaymentMethods([]);
                }
            }
        } catch (error) {
            console.error("Error fetching payment methods:", error);
            setPaymentMethods([]);
        } finally {
            setLoadingMethods(false);
        }
    };

    const openInvoiceModal = (invoice) => {
        setSelectedInvoice(invoice);
        setShowInvoiceModal(true);
    };

    const getTypeIcon = (type) => {
        const typeLower = type?.toLowerCase() || '';
        if (typeLower.includes('consultancy')) return <FaStethoscope className="text-info" />;
        if (typeLower.includes('vaccine')) return <FaSyringe className="text-success" />;
        if (typeLower.includes('lab')) return <FaFlask className="text-info" />;
        if (typeLower.includes('radiology')) return <FaXRay className="text-primary" />;
        if (typeLower.includes('surgery')) return <FaCut className="text-danger" />;
        if (typeLower.includes('deworming')) return <FaPaw className="text-warning" />;
        if (typeLower.includes('prescription')) return <FaPills className="text-secondary" />;
        if (typeLower.includes('grooming')) return <FaCut className="text-info" />;
        return <FaHeartbeat className="text-muted" />;
    };

    const getTypeBadgeColor = (type) => {
        const typeLower = type?.toLowerCase() || '';
        if (typeLower.includes('consultancy')) return 'info';
        if (typeLower.includes('vaccine')) return 'success';
        if (typeLower.includes('lab')) return 'info';
        if (typeLower.includes('radiology')) return 'primary';
        if (typeLower.includes('surgery')) return 'danger';
        if (typeLower.includes('deworming')) return 'warning';
        if (typeLower.includes('prescription')) return 'secondary';
        if (typeLower.includes('grooming')) return 'info';
        return 'dark';
    };

    const calculateGrandTotal = (invoice) => {
        if (!invoice || !invoice.detail) return 0;

        let total = 0;
        invoice.detail.forEach(item => {
            if (item.formatted_detail && item.formatted_detail.price) {
                total += parseFloat(item.formatted_detail.price) || 0;
            }
        });
        return total;
    };

    const calculatePaidTotal = (invoice) => {
        if (!invoice || !invoice.payment) return 0;

        let paid = 0;
        invoice.payment.forEach(payment => {
            if (payment.amount) {
                paid += parseFloat(payment.amount) || 0;
            }
        });
        return paid;
    };

    const getRemainingAmount = (invoice) => {
        return calculateGrandTotal(invoice) - calculatePaidTotal(invoice);
    };

    const getStatusBadge = (status) => {
        const statusConfig = {
            scheduled: { class: 'mc-badge mc-badge-primary', text: 'Scheduled' },
            pending: { class: 'mc-badge mc-badge-warning', text: 'Pending' },
            completed: { class: 'mc-badge mc-badge-success', text: 'Completed' },
            cancelled: { class: 'mc-badge mc-badge-danger', text: 'Cancelled' }
        };
        const config = statusConfig[status?.toLowerCase()] || statusConfig.pending;
        return <span className={config.class}>{config.text}</span>;
    };

    const formatDate = (dateString) => {
        if (!dateString) return 'N/A';
        try {
            const date = new Date(dateString);
            return date.toLocaleDateString();
        } catch {
            return 'N/A';
        }
    };

    const formatDateTime = (dateString) => {
        if (!dateString) return 'N/A';
        try {
            const date = new Date(dateString);
            if (isNaN(date.getTime())) return 'N/A';

            const day = date.getDate().toString().padStart(2, '0');
            const month = (date.getMonth() + 1).toString().padStart(2, '0');
            const year = date.getFullYear();

            let hours = date.getHours();
            const minutes = date.getMinutes().toString().padStart(2, '0');
            const ampm = hours >= 12 ? 'PM' : 'AM';
            hours = hours % 12;
            hours = hours ? hours : 12;

            return `${day}-${month}-${year} ${hours}:${minutes} ${ampm}`;
        } catch (error) {
            return 'N/A';
        }
    };

    const handleAddPaymentRow = () => {
        setPayments([...payments, { payment_method_id: "", amount: "", isEditing: false, id: null }]);
    };

    const handleRemovePaymentRow = (index) => {
        const newPayments = payments.filter((_, i) => i !== index);
        setPayments(newPayments);
    };

    const handlePaymentChange = (index, field, value) => {
        const newPayments = [...payments];
        newPayments[index][field] = value;
        setPayments(newPayments);
    };

    const handleEditExistingPayment = (payment) => {
        setShowPaymentModal(false);
        setTimeout(() => {
            setEditingPaymentId(payment.id);
            setPayments([{
                id: payment.id,
                payment_method_id: payment.payment_method_id,
                amount: payment.amount,
                isEditing: true
            }]);
            setShowPaymentModal(true);
        }, 100);
    };

    const handleCancelEdit = () => {
        setEditingPaymentId(null);
        setPayments([{ payment_method_id: "", amount: "", isEditing: false, id: null }]);
    };

    const handlePaymentSubmit = async () => {
        const editedPayments = payments.filter(p => p.isEditing && p.id);
        const newPayments = payments.filter(p => !p.isEditing && p.payment_method_id && p.amount && parseFloat(p.amount) > 0);

        const validNewPayments = newPayments
            .filter(p => p.payment_method_id && p.amount && parseFloat(p.amount) > 0)
            .map(p => ({
                payment_method_id: parseInt(p.payment_method_id),
                amount: parseFloat(p.amount)
            }));

        const validEditedPayments = editedPayments
            .filter(p => p.id && p.payment_method_id && p.amount && parseFloat(p.amount) > 0)
            .map(p => ({
                id: p.id,
                payment_method_id: parseInt(p.payment_method_id),
                amount: parseFloat(p.amount)
            }));

        const allPayments = [...validNewPayments, ...validEditedPayments];

        if (allPayments.length === 0) {
            Swal.fire({
                icon: 'warning',
                title: 'Warning',
                text: 'Please add at least one payment',
                confirmButtonColor: '#3085d6'
            });
            return;
        }

        const currentPaidTotal = calculatePaidTotal(selectedInvoice);
        const editedPaymentsTotal = validEditedPayments.reduce((sum, p) => sum + p.amount, 0);
        const newPaymentsTotal = validNewPayments.reduce((sum, p) => sum + p.amount, 0);

        const originalEditedAmounts = editedPayments.reduce((sum, p) => {
            const originalPayment = selectedInvoice?.payment?.find(pay => pay.id === p.id);
            return sum + (originalPayment ? parseFloat(originalPayment.amount) : 0);
        }, 0);

        const totalAfterChanges = currentPaidTotal - originalEditedAmounts + editedPaymentsTotal + newPaymentsTotal;
        const grandTotal = calculateGrandTotal(selectedInvoice);

        if (totalAfterChanges > grandTotal) {
            Swal.fire({
                icon: 'error',
                title: 'Error',
                text: 'Total payment amount exceeds grand total',
                confirmButtonColor: '#3085d6'
            });
            return;
        }

        setSubmitting(true);
        const token = localStorage.getItem("authToken");

        try {
            const payload = { payments: allPayments };

            const response = await axios.post(
                `${process.env.REACT_APP_API_URL}/pet-invoice-add-payment/${selectedInvoice.id}`,
                payload,
                { headers: { Authorization: `Bearer ${token}` } }
            );

            if (response.data.success) {
                Swal.fire({
                    icon: 'success',
                    title: 'Success',
                    text: 'Payment processed successfully',
                    timer: 2000,
                    showConfirmButton: true,
                    confirmButtonColor: '#3085d6'
                });
                
                // Update selected invoice with latest data from API response
                if (response.data.invoice) {
                    setSelectedInvoice(response.data.invoice);
                }
                
                setShowPaymentModal(false);
                setEditingPaymentId(null);
                setPayments([{ payment_method_id: "", amount: "", isEditing: false, id: null }]);
                
                // Refresh invoices list in background
                fetchInvoices();
            } else {
                Swal.fire({
                    icon: 'error',
                    title: 'Error',
                    text: response.data.message || 'Payment failed',
                    confirmButtonColor: '#3085d6'
                });
            }
        } catch (error) {
            console.error("Error processing payment:", error);
            Swal.fire({
                icon: 'error',
                title: 'Error',
                text: error.response?.data?.message || 'Payment failed',
                confirmButtonColor: '#3085d6'
            });
        } finally {
            setSubmitting(false);
        }
    };

    const handlePrint = () => {
        const printWindow = window.open(
            `${process.env.REACT_APP_URL}/invoice-print/${selectedInvoice?.id}`,
            '_blank'
        );
        if (printWindow) {
            printWindow.focus();
        }
    };

    const handleDownloadPDF = () => {
        Swal.fire({
            icon: 'info',
            title: 'Info',
            text: 'PDF download coming soon',
            confirmButtonColor: '#3085d6'
        });
    };

    const getFlatItems = (invoice) => {
        const items = [];
        if (!invoice || !invoice.detail) return items;

        invoice.detail.forEach((item, index) => {
            let detailData = null;

            if (item.formatted_detail) {
                detailData = item.formatted_detail;
            }

            if (detailData) {
                const price = parseFloat(detailData.price) || 0;
                // Skip items with 0 amount
                if (price === 0 || price === 0.00) {
                    return;
                }
                
                items.push({
                    serialNo: index + 1,
                    id: detailData.id,
                    type: detailData.type || detailData.name || 'Service',
                    name: detailData.name || 'N/A',
                    quantity: 1,
                    unitPrice: price,
                    total: price
                });
            }
        });

        return items;
    };

    const paginatedInvoices = invoices.slice((currentPage - 1) * perPage, currentPage * perPage);

    const handlePageChange = (newPage) => {
        if (newPage >= 1 && newPage <= lastPage) {
            setCurrentPage(newPage);
        }
    };

    if (loading && currentPage === 1) {
        return (
            <PageLayout>
                <div className="text-center py-5">
                    <div className="spinner-border text-primary" role="status">
                        <span className="visually-hidden">Loading...</span>
                    </div>
                    <p className="mt-3 mb-0">Loading invoices...</p>
                </div>
            </PageLayout>
        );
    }

    return (
        <PageLayout>
            <Row>
                <Col xl={12}>
                    <div className="mc-card">
                        <div className="mc-breadcrumb">
                            <h3 className="mc-breadcrumb-title">Invoices</h3>
                            <ul className="mc-breadcrumb-list">
                                <li className="mc-breadcrumb-item">
                                    <Link to="#" className="mc-breadcrumb-link">Home</Link>
                                </li>
                                <li className="mc-breadcrumb-item">
                                    <Link to="#" className="mc-breadcrumb-link">Appointments</Link>
                                </li>
                                <li className="mc-breadcrumb-item">Invoices</li>
                            </ul>
                        </div>
                    </div>
                </Col>

                {/* Table Section */}
                <Col xl={12}>
                    <div className="mc-card">
                        <div className="mc-card-header d-flex justify-content-between align-items-center">
                            <h5 className="mc-card-title mb-0">Invoices List</h5>
                            <span className="mc-badge mc-badge-info">
                                Total: {total}
                            </span>
                        </div>

                        <Row>
                            <Col xl={12}>
                                <div className="mc-card">
                                    <div className="table-responsive-wrapper">
                                        <Table responsive className="table mb-0">
                                            <thead>
                                                <tr>
                                                    <th>
                                                        <div className="mc-table-check">
                                                            <p>#</p>
                                                        </div>
                                                    </th>
                                                    <th>Appointment Number</th>
                                                    <th>Pet Name</th>
                                                    <th>Doctor</th>
                                                    <th>Appointment Date</th>
                                                    <th>Appointment Status</th>
                                                    <th width="15%">Payment Status</th>
                                                    <th>Actions</th>
                                                </tr>
                                            </thead>
                                            <tbody>
                                                {paginatedInvoices.length === 0 ? (
                                                    <tr>
                                                        <td colSpan="8" className="text-center">
                                                            No invoices found
                                                        </td>
                                                    </tr>
                                                ) : (
                                                    paginatedInvoices.map((invoice, index) => {
                                                        const remainingAmount = calculateGrandTotal(invoice) - calculatePaidTotal(invoice);
                                                        const isPaid = remainingAmount <= 0;

                                                        return (
                                                            <tr key={invoice.id}>
                                                                <td>
                                                                    <div className="mc-table-check">
                                                                        <p>{(currentPage - 1) * perPage + index + 1}</p>
                                                                    </div>
                                                                </td>
                                                                <td>
                                                                    <span className="mc-badge mc-badge-primary">
                                                                        {invoice.appointment_number || 'N/A'}
                                                                    </span>
                                                                </td>
                                                                <td>
                                                                    <div className="d-flex flex-column">
                                                                        <strong className="d-flex align-items-center gap-2">
                                                                            <FaPaw className="text-primary" />
                                                                            {invoice.pet?.name || 'N/A'}
                                                                        </strong>
                                                                        <small className="text-muted">
                                                                            Owner: {invoice.pet?.customer?.name || 'N/A'}
                                                                        </small>
                                                                    </div>
                                                                </td>
                                                                <td>
                                                                    <div className="d-flex align-items-center gap-1">
                                                                        <FaUserMd className="text-secondary" />
                                                                        {invoice.doctor?.name || 'N/A'}
                                                                    </div>
                                                                </td>
                                                                <td>
                                                                    <div className="small">
                                                                        <div className="d-flex align-items-center gap-1">
                                                                            <MdDateRange className="text-muted" />
                                                                            {formatDate(invoice.appointment_date)}
                                                                        </div>
                                                                        <div className="d-flex align-items-center gap-1">
                                                                            <MdAccessTime className="text-muted" />
                                                                            {invoice.appointment_time_slot || 'N/A'}
                                                                        </div>
                                                                    </div>
                                                                </td>
                                                                <td>{invoice.status}</td>
                                                                <td>
                                                                    <span className={`badge rounded-pill bg-${isPaid ? "success" : "danger"}`}>
                                                                        {isPaid ? "Paid" : "Unpaid"}
                                                                    </span>
                                                                </td>
                                                                <td>
                                                                    <div className="mc-table-action">
                                                                        {hasPermission("Pet invoice edit") && (

                                                                            <ButtonComponent
                                                                                type="button"
                                                                                className="material-icons view"
                                                                                onClick={() => openInvoiceModal(invoice)}
                                                                                title="View Invoice"
                                                                            >
                                                                                visibility
                                                                            </ButtonComponent>
                                                                        )}
                                                                    </div>
                                                                </td>
                                                            </tr>
                                                        );
                                                    })
                                                )}
                                            </tbody>
                                        </Table>

                                        {/* Pagination */}
                                        {lastPage > 1 && (
                                            <div className="d-flex justify-content-between align-items-center mt-3 pt-3 border-top px-3 pb-3">
                                                <div className="text-muted small">
                                                    Showing {(currentPage - 1) * perPage + 1} to {Math.min(currentPage * perPage, total)} of {total} entries
                                                </div>
                                                <div className="d-flex gap-2">
                                                    <ButtonComponent
                                                        className="btn btn-sm btn-outline-secondary"
                                                        onClick={() => handlePageChange(currentPage - 1)}
                                                        disabled={currentPage === 1}
                                                    >
                                                        Previous
                                                    </ButtonComponent>
                                                    <span className="px-3 py-1 bg-light rounded">
                                                        Page {currentPage} of {lastPage}
                                                    </span>
                                                    <ButtonComponent
                                                        className="btn btn-sm btn-outline-secondary"
                                                        onClick={() => handlePageChange(currentPage + 1)}
                                                        disabled={currentPage === lastPage}
                                                    >
                                                        Next
                                                    </ButtonComponent>
                                                </div>
                                            </div>
                                        )}
                                    </div>
                                </div>
                            </Col>
                        </Row>
                    </div>
                </Col>
            </Row>

            {/* Invoice Detail Modal */}
            <Modal
                show={showInvoiceModal}
                onHide={() => {
                    setShowInvoiceModal(false);
                    setSelectedInvoice(null);
                }}
                size="lg"
                dialogClassName="invoice-modal-dialog"
            >
                <Modal.Header closeButton className="invoice-modal-header p-3">
                    <Modal.Title className="invoice-modal-title">
                        <FaFileInvoice className="me-2" />
                        Invoice #{selectedInvoice?.id}
                    </Modal.Title>
                </Modal.Header>
                <Modal.Body className="invoice-modal-body p-3">
                    {selectedInvoice && (
                        <>
                            {/* Invoice Header */}
                            <div className="mb-4 pb-3 border-bottom">
                                <div className="d-flex justify-content-between align-items-center flex-wrap">
                                    <div>
                                        <div className="d-flex gap-4 mt-2 flex-wrap">
                                            <div>
                                                <MdDateRange className="me-1 text-muted" />
                                                <strong>Date:</strong> {formatDate(selectedInvoice.appointment_date)}
                                            </div>
                                            <div>
                                                <MdAccessTime className="me-1 text-muted" />
                                                <strong>Time:</strong> {selectedInvoice.appointment_time_slot || 'N/A'}
                                            </div>
                                            <div>
                                                <strong>Appointment:</strong> {selectedInvoice.appointment_number}
                                            </div>
                                        </div>
                                    </div>
                                    <Badge
                                        bg={getRemainingAmount(selectedInvoice) <= 0 ? "success" : "warning"}
                                        className="fs-6 px-4 py-2 mt-2 mt-md-0"
                                    >
                                        {getRemainingAmount(selectedInvoice) <= 0 ? "Paid" : "Unpaid"}
                                    </Badge>
                                </div>
                            </div>

                            {/* Customer and Pet Info */}
                            <div className="row mb-4">
                                <div className="col-md-6">
                                    <div className="border rounded p-3 bg-light">
                                        <h6 className="mb-2">Customer Information</h6>
                                        <p className="mb-1"><strong>Name:</strong> {selectedInvoice.pet?.customer?.name || 'N/A'}</p>
                                        <p className="mb-1"><strong>Email:</strong> {selectedInvoice.pet?.customer?.email || 'N/A'}</p>
                                        <p className="mb-0"><strong>Phone:</strong> {selectedInvoice.pet?.customer?.phone || 'N/A'}</p>
                                    </div>
                                </div>
                                <div className="col-md-6">
                                    <div className="border rounded p-3 bg-light">
                                        <h6 className="mb-2">Pet Information</h6>
                                        <p className="mb-1"><strong>Name:</strong> {selectedInvoice.pet?.name || 'N/A'}</p>
                                        <p className="mb-1"><strong>Breed:</strong> {selectedInvoice.pet?.breed?.name || 'N/A'}</p>
                                        <p className="mb-0"><strong>Age:</strong> {selectedInvoice.pet?.age || 'N/A'} years</p>
                                    </div>
                                </div>
                            </div>

                            {/* Invoice Table */}
                            {getFlatItems(selectedInvoice).length > 0 ? (
                                <div className="mc-table-responsive">
                                    <table className="mc-table">
                                        <thead className="mc-table-head">
                                            <tr>
                                                <th style={{ width: '5%' }}>#</th>
                                                <th style={{ width: '20%' }}>Type</th>
                                                <th style={{ width: '40%' }}>Name</th>
                                                <th style={{ width: '10%' }}>Qty</th>
                                                <th className="text-end" style={{ width: '15%' }}>Total</th>
                                            </tr>
                                        </thead>
                                        <tbody className="mc-table-body">
                                            {getFlatItems(selectedInvoice).map((item) => (
                                                <tr key={item.serialNo}>
                                                    <td>{item.serialNo}</td>
                                                    <td>
                                                        <div className="d-flex align-items-center gap-2">
                                                            {getTypeIcon(item.type)}
                                                            <Badge bg={getTypeBadgeColor(item.type)} pill>
                                                                {item.type}
                                                            </Badge>
                                                        </div>
                                                    </td>
                                                    <td>{item.name}</td>
                                                    <td className="text-center">{item.quantity}</td>
                                                    <td className="text-end fw-bold">{item.total.toFixed(2)}</td>
                                                </tr>
                                            ))}
                                        </tbody>
                                    </table>
                                </div>
                            ) : (
                                <Alert variant="info" className="text-center">
                                    No items found in invoice
                                </Alert>
                            )}

                            {/* Invoice Summary */}
                            <div className="invoice-summary mt-4">
                                <ul className="invoice-list">
                                    <li>
                                        <span className="title">Sub Total</span>
                                        <span className="clone">:</span>
                                        <span className="digit">{calculateGrandTotal(selectedInvoice).toFixed(2)}</span>
                                    </li>
                                    <li>
                                        <span className="title">Total Paid</span>
                                        <span className="clone">:</span>
                                        <span className="digit text-success">{calculatePaidTotal(selectedInvoice).toFixed(2)}</span>
                                    </li>
                                    <li>
                                        <span className="title">Pending Amount</span>
                                        <span className="clone">:</span>
                                        <span className="digit text-danger">{getRemainingAmount(selectedInvoice).toFixed(2)}</span>
                                    </li>
                                    <li>
                                        <span className="title">Payment Status</span>
                                        <span className="clone">:</span>
                                        <span className={`status ${getRemainingAmount(selectedInvoice) <= 0 ? "success" : "warning"}`}>
                                            {getRemainingAmount(selectedInvoice) <= 0 ? "Paid" : "Pending"}
                                        </span>
                                    </li>
                                </ul>
                            </div>

                            {/* Payment History with Edit Button */}
                            {selectedInvoice.payment && selectedInvoice.payment.length > 0 && (
                                <div className="mt-4">
                                    <h6 className="mb-3">Payment History</h6>
                                    <div className="mc-table-responsive">
                                        <table className="mc-table table-sm">
                                            <thead>
                                                <tr>
                                                    <th>Date</th>
                                                    <th>Method</th>
                                                    <th className="text-end">Amount</th>
                                                    <th className="text-center" style={{ width: '10%' }}>Action</th>
                                                </tr>
                                            </thead>
                                            <tbody>
                                                {selectedInvoice.payment.map((payment, idx) => (
                                                    <tr key={idx}>
                                                        <td>{formatDateTime(payment.payment_date)}</td>
                                                        <td>{payment.method?.name || 'N/A'}</td>
                                                        <td className="text-end fw-bold">{parseFloat(payment.amount).toFixed(2)}</td>
                                                        <td className="text-center">
                                                            <ButtonComponent
                                                                type="button"
                                                                className="btn btn-sm btn-primary"
                                                                onClick={() => handleEditExistingPayment(payment)}
                                                                title={getRemainingAmount(selectedInvoice) <= 0 ? "Invoice is locked" : "Edit Payment"}
                                                                disabled={getRemainingAmount(selectedInvoice) <= 0}
                                                                style={{ padding: '4px 8px' }}
                                                            >
                                                                <FaEdit />
                                                            </ButtonComponent>
                                                        </td>
                                                    </tr>
                                                ))}
                                            </tbody>
                                        </table>
                                    </div>
                                </div>
                            )}

                            {/* Action Buttons */}
                            <div className="invoice-btns mt-4 pt-3 border-top">
                                <ButtonComponent
                                    onClick={handlePrint}
                                    className="btn btn-dark d-flex align-items-center gap-2"
                                >
                                    <FaPrint /> Print Receipt
                                </ButtonComponent>

                                <ButtonComponent
                                    onClick={() => {
                                        setEditingPaymentId(null);
                                        setPayments([{ payment_method_id: "", amount: "", isEditing: false, id: null }]);
                                        setShowPaymentModal(true);
                                    }}
                                    className="btn btn-success d-flex align-items-center gap-2"
                                    disabled={getRemainingAmount(selectedInvoice) <= 0}
                                    title={getRemainingAmount(selectedInvoice) <= 0 ? "Invoice is already paid" : "Make Payment"}
                                >
                                    <FaMoneyBillWave /> Make Payment
                                </ButtonComponent>
                            </div>
                        </>
                    )}
                </Modal.Body>
            </Modal>

            {/* Payment Modal */}
            <Modal
                show={showPaymentModal}
                onHide={() => {
                    setShowPaymentModal(false);
                    setEditingPaymentId(null);
                    setPayments([{ payment_method_id: "", amount: "", isEditing: false, id: null }]);
                }}
                size="lg"
            >
                <Modal.Header closeButton className="payment-modal-header p-3">
                    <Modal.Title className="payment-modal-title">
                        <FaMoneyBillWave className="me-2 text-success" />
                        Make Payment
                    </Modal.Title>
                </Modal.Header>

                <Modal.Body className="payment-modal-body">
                    {selectedInvoice && (
                        <>
                            {/* Balance Summary */}
                            <Alert variant="info" className="mb-4">
                                <Row>
                                    <Col md={6}>
                                        <small className="text-muted d-block">Total Amount</small>
                                        <h4 className="mb-0 text-primary fw-bold">{calculateGrandTotal(selectedInvoice).toFixed(2)}</h4>
                                    </Col>
                                    <Col md={6}>
                                        <small className="text-muted d-block">Remaining Balance</small>
                                        <h4 className="mb-0 text-danger fw-bold">{getRemainingAmount(selectedInvoice).toFixed(2)}</h4>
                                    </Col>
                                </Row>
                            </Alert>

                            {/* Payment Methods Selection */}
                            <div className="d-flex justify-content-between align-items-center mb-3">
                                <h6 className="mb-0 fw-semibold">Payments</h6>
                                <ButtonComponent
                                    className="btn btn-primary btn-sm"
                                    onClick={handleAddPaymentRow}
                                >
                                    <FaPlus className="me-1" size={12} /> Add Payment
                                </ButtonComponent>
                            </div>

                            {payments.map((payment, index) => (
                                <div
                                    key={index}
                                    className={`border rounded p-3 mb-3 ${payment.isEditing ? 'bg-warning bg-opacity-10' : ''}`}
                                    style={{ borderLeft: payment.isEditing ? '4px solid #ffc107' : '1px solid #dee2e6' }}
                                >
                                    <div className="d-flex justify-content-between mb-2">
                                        <strong className={payment.isEditing ? 'text-warning' : ''}>
                                            {payment.isEditing ? 'Edit Payment' : 'New Payment'} #{index + 1}
                                        </strong>
                                        <div>
                                            {payment.isEditing && (
                                                <ButtonComponent
                                                    className="btn btn-link btn-sm text-secondary me-2 p-0"
                                                    onClick={handleCancelEdit}
                                                    title="Cancel Edit"
                                                >
                                                    <FaTimes />
                                                </ButtonComponent>
                                            )}
                                            {payments.length > 1 && !payment.isEditing && (
                                                <ButtonComponent
                                                    className="btn btn-link btn-sm text-danger p-0"
                                                    onClick={() => handleRemovePaymentRow(index)}
                                                    title="Remove Payment"
                                                >
                                                    <FaTrashAlt />
                                                </ButtonComponent>
                                            )}
                                        </div>
                                    </div>
                                    <Row>
                                        <Col md={6}>
                                            <Form.Group className="mb-2">
                                                <Form.Label className="fw-medium small">Payment Method</Form.Label>
                                                <Form.Select
                                                    value={payment.payment_method_id}
                                                    onChange={(e) => handlePaymentChange(index, 'payment_method_id', e.target.value)}
                                                    disabled={loadingMethods}
                                                    required
                                                >
                                                    <option value="">Select Payment Method</option>
                                                    {paymentMethods.map(method => (
                                                        <option key={method.id} value={method.id}>
                                                            {method.name}
                                                        </option>
                                                    ))}
                                                </Form.Select>
                                            </Form.Group>
                                        </Col>
                                        <Col md={6}>
                                            <Form.Group className="mb-2">
                                                <Form.Label className="fw-medium small">Amount</Form.Label>
                                                <Form.Control
                                                    type="number"
                                                    step="0.01"
                                                    min="0"
                                                    max={getRemainingAmount(selectedInvoice) + (payment.isEditing ? parseFloat(payment.amount) : 0)}
                                                    value={payment.amount}
                                                    onChange={(e) => handlePaymentChange(index, 'amount', e.target.value)}
                                                    placeholder="0.00"
                                                    required
                                                />
                                            </Form.Group>
                                        </Col>
                                    </Row>
                                </div>
                            ))}

                            {/* Total Payment Preview */}
                            {payments.some(p => p.amount && parseFloat(p.amount) > 0) && (
                                <Alert variant="secondary" className="mb-0">
                                    <div className="d-flex justify-content-between align-items-center">
                                        <strong>Total to Pay:</strong>
                                        <strong className="text-primary">
                                            {payments.reduce((sum, p) => sum + (parseFloat(p.amount) || 0), 0).toFixed(2)}
                                        </strong>
                                    </div>
                                </Alert>
                            )}
                        </>
                    )}
                </Modal.Body>

                <Modal.Footer className="payment-modal-footer p-3">
                    <ButtonComponent
                        className="btn btn-secondary"
                        onClick={() => {
                            setShowPaymentModal(false);
                            setEditingPaymentId(null);
                            setPayments([{ payment_method_id: "", amount: "", isEditing: false, id: null }]);
                        }}
                    >
                        Cancel
                    </ButtonComponent>
                    <ButtonComponent
                        className="btn btn-success"
                        onClick={handlePaymentSubmit}
                        disabled={submitting}
                    >
                        {submitting ? (
                            <>
                                <Spinner animation="border" size="sm" className="me-2" />
                                Processing
                            </>
                        ) : (
                            <>
                                <FaSave className="me-2" />
                                Complete Payment
                            </>
                        )}
                    </ButtonComponent>
                </Modal.Footer>
            </Modal>

            <style jsx>{`
                .invoice-modal-dialog {
                    max-width: 900px;
                }
                .invoice-modal-header,
                .payment-modal-header {
                    background-color: #f8f9fa;
                    border-bottom: 2px solid #dee2e6;
                }
                .invoice-modal-title,
                .payment-modal-title {
                    font-weight: 600;
                }
                .invoice-modal-body,
                .payment-modal-body {
                    padding: 20px;
                    max-height: 70vh;
                    overflow-y: auto;
                }
                .invoice-summary .invoice-list {
                    list-style: none;
                    padding: 0;
                    margin: 0;
                    background: #f8f9fa;
                    border-radius: 8px;
                    padding: 15px;
                }
                .invoice-summary .invoice-list li {
                    display: flex;
                    justify-content: flex-end;
                    align-items: center;
                    margin-bottom: 10px;
                    padding: 5px 0;
                }
                .invoice-summary .invoice-list li:last-child {
                    margin-bottom: 0;
                }
                .invoice-summary .invoice-list li .title {
                    width: 150px;
                    font-weight: 500;
                    text-align: right;
                }
                .invoice-summary .invoice-list li .clone {
                    width: 20px;
                    text-align: center;
                }
                .invoice-summary .invoice-list li .digit {
                    width: 120px;
                    text-align: right;
                    font-weight: 600;
                }
                .invoice-summary .invoice-list li .status {
                    width: 120px;
                    text-align: right;
                    font-weight: 600;
                    padding: 4px 8px;
                    border-radius: 4px;
                }
                .invoice-summary .invoice-list li .status.success {
                    background-color: #d4edda;
                    color: #155724;
                }
                .invoice-summary .invoice-list li .status.warning {
                    background-color: #fff3cd;
                    color: #856404;
                }
                .invoice-btns {
                    display: flex;
                    gap: 1rem;
                    flex-wrap: wrap;
                }
                .invoice-btns button {
                    transition: all 0.3s ease;
                }
                .invoice-btns button:hover {
                    transform: translateY(-2px);
                    box-shadow: 0 4px 8px rgba(0,0,0,0.1);
                }
                .mc-table-action .material-icons.view {
                    color: #3182ce;
                    cursor: pointer;
                    font-size: 20px;
                }
                .mc-badge {
                    display: inline-flex;
                    align-items: center;
                    gap: 5px;
                    padding: 4px 8px;
                    border-radius: 4px;
                    font-size: 12px;
                    font-weight: 500;
                }
                .mc-badge-success {
                    background-color: #d4edda;
                    color: #155724;
                }
                .mc-badge-danger {
                    background-color: #f8d7da;
                    color: #721c24;
                }
                .mc-badge-warning {
                    background-color: #fff3cd;
                    color: #856404;
                }
                .mc-badge-info {
                    background-color: #d1ecf1;
                    color: #0c5460;
                }
                .mc-badge-primary {
                    background-color: #cfe2ff;
                    color: #084298;
                }
                @media print {
                    .invoice-btns,
                    .mc-table-action,
                    .modal-header .btn-close {
                        display: none !important;
                    }
                }
            `}</style>
        </PageLayout>
    );
}